import { errorFromResponse, notifications } from '~/app/notifications'
import { getters, actions as parentActions, mutations as parentMutations, state } from '~/sites/shop/store/cart'
import { hookDefinitions, hookDispatch } from '@/hooks'
import eventBus from '@/events/eventBus'
import eventDefinitions from '@/events/eventDefinitions'
import jsCookie from 'js-cookie'

const checkoutRouteNames = new Set(['checkout', 'checkout-methods', 'checkout-info'])
const checkoutRoutePaths = new Set([
  '/checkout',
  '/checkout/methods',
  '/checkout/info',
  '/kosik',
  '/kosik/doprava-a-platba',
  '/kosik/dodaci-udaje',
])
const cartPlaceProfilingQueryKey = 'cartPlaceProfile'
const cartPlaceProfilingSessionKey = 'inspishop.cartPlaceProfile'
const cartPlaceProfilingHeader = 'X-Inspishop-Cart-Place-Profile'
const cartFetchProfilingQueryKey = 'cartProfile'
const cartFetchProfilingSessionKey = 'inspishop.cartProfile'
const cartFetchResponseMode = 'compact-tree'
const cartFetchPath = '/api/v1/cart/compact'
const guestCartBackendCookie = 'X-Inspishop-Guest-Cart'
const guestCartStorageCookie = 'X-Inspishop-Guest-Cart-Token'
const guestCartCookieOptions = { path: '/' }
const guestCartStorageKey = 'inspishop.guestCartToken'
const guestCartAxiosHeader = 'x-inspishop-guest-cart'
const userTokenCookie = 'X-Inspishop-User-Token'
const cartFetchTimeoutMs = 15000
const cartProductEnrichmentTimeoutMs = 4000
const cartItemAnalyticsDebounceMs = 700

function getClientCookie(name) {
  if (!process.client) {
    return null
  }

  return jsCookie.get(name)
}

function getClientStorage(name) {
  if (!process.client) {
    return null
  }

  try {
    return window.localStorage.getItem(name)
  } catch (e) {
    return null
  }
}

function setClientStorage(name, value) {
  if (!process.client) {
    return
  }

  try {
    if (value) {
      window.localStorage.setItem(name, value)
    } else {
      window.localStorage.removeItem(name)
    }
  } catch (e) {
    // Ignore storage availability issues and keep cookie persistence as fallback.
  }
}

export function isCheckoutRoute(route) {
  const routeName = (route?.name || '').split('___')[0]
  const routePath = (route?.path || '').replace(/\/+$/, '')

  return checkoutRouteNames.has(routeName) || checkoutRoutePaths.has(routePath)
}

function getCurrentRoute(app) {
  return app?.context?.route || app?.router?.currentRoute
}

function getAddToCartRequestConfig(app) {
  const route = getCurrentRoute(app)

  if (isCheckoutRoute(route)) {
    return undefined
  }

  const requestConfig = {
    headers: {
      'X-Inspishop-Cart-Response-Mode': 'summary-lite',
    },
  }

  if (isCartFetchProfilingEnabled(app)) {
    requestConfig.params = {
      [cartFetchProfilingQueryKey]: '1',
    }
  }

  return requestConfig
}

function isTruthyFlag(value) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return (
    normalizedValue === '' ||
    normalizedValue === true ||
    normalizedValue === 1 ||
    normalizedValue === '1' ||
    normalizedValue === 'true' ||
    normalizedValue === 'on' ||
    normalizedValue === 'yes'
  )
}

function isFalsyFlag(value) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return (
    normalizedValue === false ||
    normalizedValue === 0 ||
    normalizedValue === '0' ||
    normalizedValue === 'false' ||
    normalizedValue === 'off' ||
    normalizedValue === 'no'
  )
}

function persistCartPlaceProfilingFlag(enabled) {
  if (!process.client) {
    return
  }

  try {
    if (enabled) {
      window.sessionStorage.setItem(cartPlaceProfilingSessionKey, '1')
    } else {
      window.sessionStorage.removeItem(cartPlaceProfilingSessionKey)
    }
  } catch (e) {
    // Ignore session storage availability issues and fall back to current-request behavior only.
  }
}

function isCartPlaceProfilingEnabled(app) {
  const route = getCurrentRoute(app)
  const queryValue = route?.query?.[cartPlaceProfilingQueryKey]

  if (isTruthyFlag(queryValue)) {
    persistCartPlaceProfilingFlag(true)

    return true
  }

  if (isFalsyFlag(queryValue)) {
    persistCartPlaceProfilingFlag(false)

    return false
  }

  if (!process.client) {
    return false
  }

  try {
    return window.sessionStorage.getItem(cartPlaceProfilingSessionKey) === '1'
  } catch (e) {
    return false
  }
}

function getPlaceOrderRequestConfig(app) {
  if (!isCartPlaceProfilingEnabled(app)) {
    return undefined
  }

  return {
    headers: {
      [cartPlaceProfilingHeader]: '1',
    },
  }
}

function persistCartFetchProfilingFlag(enabled) {
  if (!process.client) {
    return
  }

  try {
    if (enabled) {
      window.sessionStorage.setItem(cartFetchProfilingSessionKey, '1')
    } else {
      window.sessionStorage.removeItem(cartFetchProfilingSessionKey)
    }
  } catch (e) {
    // Ignore session storage availability issues and fall back to current-request behavior only.
  }
}

function isCartFetchProfilingEnabled(app) {
  const route = getCurrentRoute(app)
  const queryValue = route?.query?.[cartFetchProfilingQueryKey]

  if (isTruthyFlag(queryValue)) {
    persistCartFetchProfilingFlag(true)

    return true
  }

  if (isFalsyFlag(queryValue)) {
    persistCartFetchProfilingFlag(false)

    return false
  }

  if (!process.client) {
    return false
  }

  try {
    return window.sessionStorage.getItem(cartFetchProfilingSessionKey) === '1'
  } catch (e) {
    return false
  }
}

function getCartFetchRequestConfig(app) {
  const requestConfig = {
    timeout: cartFetchTimeoutMs,
    headers: {
      'X-Inspishop-Cart-Response-Mode': cartFetchResponseMode,
    },
  }

  if (isCartFetchProfilingEnabled(app)) {
    requestConfig.params = {
      [cartFetchProfilingQueryKey]: '1',
    }
  }

  return requestConfig
}

function getCartMutationRequestConfig(app) {
  const requestConfig = {
    headers: {
      'X-Inspishop-Cart-Response-Mode': cartFetchResponseMode,
    },
  }

  if (isCartFetchProfilingEnabled(app)) {
    requestConfig.params = {
      [cartFetchProfilingQueryKey]: '1',
    }
  }

  return requestConfig
}

function reduceOrderProductStorage(orderData) {
  if (!Array.isArray(orderData?.items)) {
    return orderData
  }

  function reduceItemProductStorage(item) {
    if (item.product) {
      item.product.content = {}

      if (item.product.attributes) {
        item.product.attributes.cms_ids = []
      }
    }

    if (Array.isArray(item.items)) {
      item.items.forEach(reduceItemProductStorage)
    }

    return item
  }

  orderData.items = orderData.items.map(reduceItemProductStorage)

  return orderData
}

function isSummaryCartResponse(cartData) {
  return !!cartData && !Array.isArray(cartData.items)
}

function hasCartPrices(cartData) {
  return !!cartData?.prices
}

function getCartRequestLocks(app) {
  if (!app.__inspishopCartRequestLocks) {
    app.__inspishopCartRequestLocks = {}
  }

  return app.__inspishopCartRequestLocks
}

function getCartItemAnalyticsState(app) {
  if (!app.__inspishopCartItemAnalyticsState) {
    app.__inspishopCartItemAnalyticsState = {}
  }

  return app.__inspishopCartItemAnalyticsState
}

function queueCartItemQuantityAnalytics(app, item, changedAmount) {
  const normalizedChangedAmount = Number(changedAmount || 0)
  const itemId = item?.id

  if (!itemId || !normalizedChangedAmount || !process.client) {
    return
  }

  const analyticsState = getCartItemAnalyticsState(app)
  const stateKey = String(itemId)
  const currentState = analyticsState[stateKey] || {
    item,
    changedAmount: 0,
    timer: null,
  }

  currentState.item = item
  currentState.changedAmount += normalizedChangedAmount

  if (currentState.timer) {
    window.clearTimeout(currentState.timer)
  }

  currentState.timer = window.setTimeout(() => {
    if (currentState.changedAmount > 0) {
      eventBus.$emit(eventDefinitions.CART.ITEM.ADD, {
        addedAmount: currentState.changedAmount,
        item: currentState.item,
      })
    }

    if (currentState.changedAmount < 0) {
      eventBus.$emit(eventDefinitions.CART.ITEM.REMOVE, {
        item: currentState.item,
        removedAmount: Math.abs(currentState.changedAmount),
      })
    }

    delete analyticsState[stateKey]
  }, cartItemAnalyticsDebounceMs)

  analyticsState[stateKey] = currentState
}

function clearCartItemQuantityAnalytics(app, itemId) {
  if (!itemId || !process.client) {
    return
  }

  const analyticsState = getCartItemAnalyticsState(app)
  const stateKey = String(itemId)
  const currentState = analyticsState[stateKey]

  if (!currentState) {
    return
  }

  if (currentState.timer) {
    window.clearTimeout(currentState.timer)
  }

  delete analyticsState[stateKey]
}

function findCartItemById(items, itemId) {
  for (const item of items || []) {
    if (item.id === itemId) {
      return item
    }

    const nestedItem = findCartItemById(item.items, itemId)

    if (nestedItem) {
      return nestedItem
    }
  }

  return null
}

function getCartItemCustomData(item) {
  return item?.attributes?.custom || item?.custom || {}
}

function normalizeCartItemConfigCodes(customData) {
  return (customData?.configuration || [])
    .map(item => item?.code)
    .filter(code => typeof code === 'string' && code !== '')
    .sort()
}

function getCartItemIdentityValues(item) {
  return [
    item?.productId,
    item?.variantId,
    item?.shoppableId,
    item?.product?.id,
    item?.variant?.id,
    item?.shoppable?.id,
    getCartItemCustomData(item)?.externalId,
  ]
    .filter(value => value !== null && value !== undefined && value !== '')
    .map(value => String(value))
}

function arraysMatch(a, b) {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

function cartItemsMatchConfiguration(item, sourceItem) {
  const itemCustom = getCartItemCustomData(item)
  const sourceCustom = getCartItemCustomData(sourceItem)

  if (!sourceCustom?.width || !sourceCustom?.height) {
    return false
  }

  if (
    String(itemCustom?.width) !== String(sourceCustom.width) ||
    String(itemCustom?.height) !== String(sourceCustom.height)
  ) {
    return false
  }

  if (
    sourceCustom.externalId &&
    itemCustom.externalId &&
    String(itemCustom.externalId) !== String(sourceCustom.externalId)
  ) {
    return false
  }

  const sourceCodes = normalizeCartItemConfigCodes(sourceCustom)
  const itemCodes = normalizeCartItemConfigCodes(itemCustom)

  if (sourceCodes.length > 0 && itemCodes.length > 0 && !arraysMatch(itemCodes, sourceCodes)) {
    return false
  }

  const sourceIdentityValues = getCartItemIdentityValues(sourceItem)
  const itemIdentityValues = getCartItemIdentityValues(item)

  if (sourceIdentityValues.length > 0 && itemIdentityValues.length > 0) {
    return sourceIdentityValues.some(value => itemIdentityValues.includes(value))
  }

  return true
}

function findCartItemByConfiguration(items, sourceItem) {
  for (const item of items || []) {
    if (cartItemsMatchConfiguration(item, sourceItem)) {
      return item
    }

    const nestedItem = findCartItemByConfiguration(item.items, sourceItem)

    if (nestedItem) {
      return nestedItem
    }
  }

  return null
}

function findCartItemByIdOrConfiguration(items, sourceItem) {
  return findCartItemById(items, sourceItem?.id) || findCartItemByConfiguration(items, sourceItem)
}

function createAddedItemLookupSource(addedItem, custom) {
  const customData = addedItem?.attributes?.custom || addedItem?.custom || custom || {}

  return {
    ...(addedItem || {}),
    custom: customData,
    attributes: {
      ...(addedItem?.attributes || {}),
      custom: customData,
    },
  }
}

function mergeCartItem(existingItem, addedItem) {
  if (!existingItem) {
    return addedItem
  }

  return {
    ...existingItem,
    ...addedItem,
    product: addedItem?.product || existingItem.product || null,
    shoppable: addedItem?.shoppable || existingItem.shoppable || null,
    variant: addedItem?.variant || existingItem.variant || null,
    items:
      Array.isArray(addedItem?.items) && addedItem.items.length > 0
        ? addedItem.items
        : Array.isArray(existingItem.items)
          ? existingItem.items
          : [],
  }
}

function mergeCartItems(existingItems, addedItem) {
  if (!addedItem) {
    return existingItems || []
  }

  let hasUpdatedExistingItem = false
  const mergedItems = (existingItems || []).map(item => {
    if (item.id !== addedItem.id) {
      return item
    }

    hasUpdatedExistingItem = true

    return mergeCartItem(item, addedItem)
  })

  if (!hasUpdatedExistingItem) {
    mergedItems.push(addedItem)
  }

  return mergedItems
}

function mergeSummaryCartData(currentCartData, summaryCartData, addedItem) {
  return {
    ...(currentCartData || {}),
    ...summaryCartData,
    items: mergeCartItems(currentCartData?.items, addedItem),
    unavailableItems: currentCartData?.unavailableItems || [],
    deferredTotals: false,
  }
}

function mergeSummaryLiteCartData(currentCartData, summaryCartData, addedItem) {
  return {
    ...(currentCartData || {}),
    ...summaryCartData,
    items: mergeCartItems(currentCartData?.items, addedItem),
    unavailableItems: currentCartData?.unavailableItems || [],
    discounts: [],
    giftIds: [],
    prices: null,
    deferredTotals: true,
  }
}

function resolveWithTimeout(promise, fallback, timeoutMs) {
  return new Promise((resolve, reject) => {
    let settled = false
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true
        resolve(fallback)
      }
    }, timeoutMs)

    promise.then(
      value => {
        if (!settled) {
          settled = true
          clearTimeout(timeout)
          resolve(value)
        }
      },
      error => {
        if (!settled) {
          settled = true
          clearTimeout(timeout)
          reject(error)
        }
      },
    )
  })
}

function getFallbackCartItemName(item) {
  return item.name || item.fullname || item.productName || item.variantName || item.title || 'Položka v košíku'
}

function getFallbackAvailability(item) {
  const existingAvailability =
    item.variant?.availability || item.shoppable?.availability || item.product?.availability || item.availability || {}

  return {
    checkStock: false,
    isAvailable: true,
    stock: 999999,
    availability: '',
    soldAvailability: '',
    amountMin: null,
    amountMax: null,
    amountStep: null,
    unit: item.unit || 'ks',
    ...existingAvailability,
  }
}

function getPriceValue(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value))) {
      return Number(value)
    }
  }

  return null
}

function getCartItemTotalWithTax(item) {
  if (!item) {
    return 0
  }

  const ownTotalWithTax = getPriceValue(
    item.prices?.totalWithTax,
    item.prices?.unitWithTax !== null && item.prices?.unitWithTax !== undefined
      ? Number(item.prices.unitWithTax) * (getPriceValue(item.amount, 1) || 1)
      : null,
  )

  if (ownTotalWithTax && ownTotalWithTax > 0) {
    return ownTotalWithTax
  }

  return (item.items || []).reduce((sum, child) => sum + getCartItemTotalWithTax(child), 0)
}

function hasPositiveCartItemPrice(item) {
  return getCartItemTotalWithTax(item) > 0
}

function getFallbackPrices(item, variant, product) {
  const amount = getPriceValue(item.amount, 1) || 1
  const existingPrices = item.prices || {}
  const sourcePrices = variant?.prices || item.shoppable?.prices || product?.prices || {}
  const unitWithTax = getPriceValue(
    existingPrices.unitWithTax,
    sourcePrices.unitWithTax,
    sourcePrices.minimumWithTax,
    sourcePrices.originalWithTax,
  )
  const unitWithoutTax = getPriceValue(
    existingPrices.unitWithoutTax,
    sourcePrices.unitWithoutTax,
    sourcePrices.minimumWithoutTax,
    sourcePrices.originalWithoutTax,
  )
  const totalWithTax = getPriceValue(existingPrices.totalWithTax, unitWithTax !== null ? unitWithTax * amount : null)
  const totalWithoutTax = getPriceValue(
    existingPrices.totalWithoutTax,
    unitWithoutTax !== null ? unitWithoutTax * amount : null,
  )

  return {
    unitWithoutTax,
    unitWithTax,
    totalWithoutTax,
    totalWithTax,
    unitOriginalWithoutTax: getPriceValue(existingPrices.unitOriginalWithoutTax, sourcePrices.originalWithoutTax),
    unitOriginalWithTax: getPriceValue(existingPrices.unitOriginalWithTax, sourcePrices.originalWithTax),
    totalOriginalWithoutTax: getPriceValue(
      existingPrices.totalOriginalWithoutTax,
      sourcePrices.originalWithoutTax !== undefined && sourcePrices.originalWithoutTax !== null
        ? Number(sourcePrices.originalWithoutTax) * amount
        : null,
    ),
    totalOriginalWithTax: getPriceValue(
      existingPrices.totalOriginalWithTax,
      sourcePrices.originalWithTax !== undefined && sourcePrices.originalWithTax !== null
        ? Number(sourcePrices.originalWithTax) * amount
        : null,
    ),
    inDiscount: existingPrices.inDiscount || sourcePrices.inDiscount || false,
    percentageDiscount: getPriceValue(existingPrices.percentageDiscount, sourcePrices.percentageDiscount, 0),
    currencyCode: existingPrices.currencyCode || sourcePrices.currencyCode,
    tax: getPriceValue(existingPrices.tax, sourcePrices.tax),
  }
}

function ensureCartItemProductData(item) {
  if (!item) {
    return item
  }

  const itemName = getFallbackCartItemName(item)
  const availability = getFallbackAvailability(item)
  const existingShoppable = item.shoppable || item.variant || {}
  const shoppable = {
    id: item.variantId || existingShoppable.id || item.id,
    productId: item.productId || existingShoppable.productId || item.product?.id || null,
    name: itemName,
    fullname: itemName,
    mainImage: item.mainImage || item.product?.mainImage || null,
    unit: item.unit || availability.unit || 'ks',
    prices: item.prices || {},
    content: { parameters: [] },
    ...existingShoppable,
    availability: {
      ...availability,
      ...(existingShoppable.availability || {}),
    },
  }
  const existingVariant = item.variant || {}
  const variant = {
    ...shoppable,
    ...existingVariant,
    availability: {
      ...shoppable.availability,
      ...(existingVariant.availability || {}),
    },
  }
  const existingProduct = item.product || {}
  const product = {
    id: item.productId || existingProduct.id || null,
    name: itemName,
    fullname: itemName,
    slug: '',
    mainImage: shoppable.mainImage,
    gifts: [],
    variants: [variant],
    attributes: {},
    availability: {
      ...availability,
      ...(existingProduct.availability || {}),
    },
    prices: item.prices || {},
    ...existingProduct,
  }

  item.shoppable = shoppable
  item.variant = variant
  item.product = {
    ...product,
    gifts: Array.isArray(product.gifts) ? product.gifts : [],
    variants: Array.isArray(product.variants) && product.variants.length > 0 ? product.variants : [variant],
    attributes: product.attributes || {},
  }
  item.prices = getFallbackPrices(item, variant, item.product)
  item.items = Array.isArray(item.items) ? item.items : []
  item.items.forEach(ensureCartItemProductData)

  return item
}

function ensureCartDataProductData(data) {
  if (data && Array.isArray(data.items)) {
    data.items.forEach(ensureCartItemProductData)
  }

  return data
}

function normalizeCartDataItems(data) {
  if (!data) {
    return data
  }

  if (Array.isArray(data.items)) {
    return data
  }

  return {
    ...data,
    items: data.items && typeof data.items === 'object' ? Object.values(data.items) : [],
  }
}

function getCartDataSummary(cartData) {
  const normalizedCartData = normalizeCartDataItems(cartData || {})
  const items = normalizedCartData?.items

  return {
    hasCartData: !!cartData,
    itemsType: Array.isArray(cartData?.items) ? 'array' : typeof cartData?.items,
    itemsCount: Array.isArray(items) ? items.length : 0,
    hasPrices: hasCartPrices(normalizedCartData),
    deferredTotals: !!normalizedCartData?.deferredTotals,
  }
}

function isCartDebugEnabled(app) {
  if (!process.client) {
    return false
  }

  const route = getCurrentRoute(app)

  try {
    if (route?.query?.cartDebug === '1') {
      window.localStorage.setItem('inspishop.cartDebug', '1')
    }

    if (route?.query?.cartDebug === '0') {
      window.localStorage.removeItem('inspishop.cartDebug')
    }

    if (window.localStorage.getItem('inspishop.cartDebug') === '1') {
      return true
    }
  } catch (e) {
    // Ignore storage availability issues; localhost logging below still works.
  }

  return ['localhost', '127.0.0.1'].includes(window.location.hostname)
}

function debugCart(app, step, payload = {}) {
  if (!isCartDebugEnabled(app)) {
    return
  }

  const route = getCurrentRoute(app)
  const context = {
    routeName: route?.name,
    routePath: route?.path,
    timestamp: new Date().toISOString(),
    ...payload,
  }

  console.info('[cart-debug]', step, JSON.stringify(context))
}

const actions = {
  ...parentActions,
  async SET_CART_INPUT_DEFAULT(actionContext) {
    const { commit, state } = actionContext

    await parentActions.SET_CART_INPUT_DEFAULT.call(this, actionContext)

    if (!state.input.billingCountry && state.input.country) {
      commit('SET_CART_INPUT_FIELD', { field: 'billingCountry', value: state.input.country })
    }
  },
  FETCH_GUEST_CART_TOKEN({ commit }) {
    if (getClientCookie(userTokenCookie)) {
      debugCart(this.app, 'FETCH_GUEST_CART_TOKEN skipped for logged user', {
        hasUserTokenCookie: true,
      })
      commit('SET_GUEST_CART_TOKEN', null)
      return
    }

    const storedToken = getClientCookie(guestCartStorageCookie) || getClientStorage(guestCartStorageKey)

    if (storedToken) {
      debugCart(this.app, 'FETCH_GUEST_CART_TOKEN from client storage', {
        hasStoredGuestToken: true,
      })
      commit('SET_GUEST_CART_TOKEN', storedToken)
      return
    }

    const backendCookie = getClientCookie(guestCartBackendCookie)

    if (backendCookie) {
      debugCart(this.app, 'FETCH_GUEST_CART_TOKEN from backend cookie', {
        hasBackendGuestToken: true,
      })
      commit('SET_GUEST_CART_TOKEN', backendCookie)
    } else {
      debugCart(this.app, 'FETCH_GUEST_CART_TOKEN no token found')
    }
  },
  SET_AXIOS_GUEST_CART_TOKEN({ state, rootState }) {
    if (state.guestCartToken && !rootState.auth.token) {
      this.$axios.defaults.headers.common[guestCartAxiosHeader] = state.guestCartToken
      debugCart(this.app, 'SET_AXIOS_GUEST_CART_TOKEN set', {
        hasGuestCartToken: true,
        hasAuthToken: false,
      })
    } else {
      delete this.$axios.defaults.headers.common[guestCartAxiosHeader]
      debugCart(this.app, 'SET_AXIOS_GUEST_CART_TOKEN cleared', {
        hasGuestCartToken: !!state.guestCartToken,
        hasAuthToken: !!rootState.auth.token,
      })
    }
  },
  CLEAR_GUEST_CART_TOKEN({ commit, dispatch }) {
    debugCart(this.app, 'CLEAR_GUEST_CART_TOKEN')
    commit('SET_GUEST_CART_TOKEN', null)
    dispatch('SET_AXIOS_GUEST_CART_TOKEN')
  },
  async FETCH_CART({ commit, state, rootState, dispatch }, { forceReload = false } = {}) {
    const startedAt = Date.now()
    const currentRoute = getCurrentRoute(this.app)

    debugCart(this.app, 'FETCH_CART start', {
      forceReload,
      hasGuestCartToken: !!state.guestCartToken,
      hasAuthToken: !!rootState.auth.token,
      isLoaded: state.isLoaded,
      cart: getCartDataSummary(state.cartData),
    })

    try {
      if (!(state.guestCartToken || rootState.auth.token)) {
        debugCart(this.app, 'FETCH_CART no identity -> SET_EMPTY_CART')
        commit('SET_EMPTY_CART')
        return
      }

      const requiresCheckoutRefresh = isCheckoutRoute(currentRoute) && state.cartData?.deferredTotals

      if (state.isLoaded && !forceReload && !requiresCheckoutRefresh) {
        debugCart(this.app, 'FETCH_CART skipped already loaded', {
          requiresCheckoutRefresh,
          cart: getCartDataSummary(state.cartData),
        })
        return
      }

      const requestLocks = getCartRequestLocks(this.app)
      const lockAlreadyExists = !!requestLocks.fetchCartPromise

      debugCart(this.app, 'FETCH_CART request lock', {
        lockAlreadyExists,
        requiresCheckoutRefresh,
      })

      if (!requestLocks.fetchCartPromise) {
        requestLocks.fetchCartPromise = (async () => {
          try {
            const requestConfig = getCartFetchRequestConfig(this.app)
            const url = this.$env.STORE_URL + cartFetchPath
            debugCart(this.app, 'FETCH_CART request start', {
              url,
              timeout: requestConfig.timeout,
              responseMode: requestConfig.headers['X-Inspishop-Cart-Response-Mode'],
              profiling: !!requestConfig.params?.[cartFetchProfilingQueryKey],
              hasGuestHeader: !!this.$axios.defaults.headers.common[guestCartAxiosHeader],
              hasAuthHeader: !!this.$axios.defaults.headers.common.Authorization,
            })
            const response = await this.$axios.$get(url, requestConfig)
            debugCart(this.app, 'FETCH_CART response', {
              durationMs: Date.now() - startedAt,
              cart: getCartDataSummary(response.data),
            })
            await dispatch('SET_CART_DATA', response.data)
            debugCart(this.app, 'FETCH_CART after SET_CART_DATA', {
              durationMs: Date.now() - startedAt,
              isLoaded: state.isLoaded,
              cart: getCartDataSummary(state.cartData),
            })
            await dispatch('SET_CART_INPUT_DEFAULT')
            debugCart(this.app, 'FETCH_CART after SET_CART_INPUT_DEFAULT', {
              durationMs: Date.now() - startedAt,
              isLoaded: state.isLoaded,
              cart: getCartDataSummary(state.cartData),
            })
          } finally {
            requestLocks.fetchCartPromise = null
            debugCart(this.app, 'FETCH_CART request lock cleared', {
              durationMs: Date.now() - startedAt,
            })
          }
        })()
      }

      await requestLocks.fetchCartPromise
      debugCart(this.app, 'FETCH_CART done', {
        durationMs: Date.now() - startedAt,
        isLoaded: state.isLoaded,
        cart: getCartDataSummary(state.cartData),
      })
    } catch (e) {
      console.error('[cart] FETCH_CART failed', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      })
      debugCart(this.app, 'FETCH_CART failed', {
        durationMs: Date.now() - startedAt,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
        isLoaded: state.isLoaded,
        cart: getCartDataSummary(state.cartData),
      })

      if (!state.isLoaded) {
        commit('SET_CART_DATA', normalizeCartDataItems(state.cartData || {}))
        debugCart(this.app, 'FETCH_CART fallback commit', {
          isLoaded: state.isLoaded,
          cart: getCartDataSummary(state.cartData),
        })
      }
    }
  },

  async FETCH_SHIPPERS({ commit, state }, { force } = {}) {
    if (state.shippers.length && !force) {
      return
    }

    try {
      const requestLocks = getCartRequestLocks(this.app)

      if (!requestLocks.fetchShippersPromise || force) {
        let fetchShippersPromise
        fetchShippersPromise = (async () => {
          try {
            const response = await this.$axios.$get(this.$env.STORE_URL + '/api/v1/cart/shippers')
            commit('SET_SHIPPERS', response.data)

            if (state.input.shipper) {
              const freshShipper = state.shippers.find(shipper => shipper.id === state.input.shipper.id)
              commit('SET_CART_INPUT_FIELD', { field: 'shipper', value: freshShipper })
            }
          } finally {
            if (requestLocks.fetchShippersPromise === fetchShippersPromise) {
              requestLocks.fetchShippersPromise = null
            }
          }
        })()

        requestLocks.fetchShippersPromise = fetchShippersPromise
      }

      await requestLocks.fetchShippersPromise
    } catch (e) {
      console.error(e)
    }
  },

  async FETCH_PAYMENTS({ commit }, { force } = {}) {
    try {
      const requestLocks = getCartRequestLocks(this.app)

      if (!requestLocks.fetchPaymentsPromise || force) {
        let fetchPaymentsPromise
        fetchPaymentsPromise = (async () => {
          try {
            const response = await this.$axios.$get(this.$env.STORE_URL + '/api/v1/cart/payments')
            commit('SET_PAYMENTS', response.data)
          } finally {
            if (requestLocks.fetchPaymentsPromise === fetchPaymentsPromise) {
              requestLocks.fetchPaymentsPromise = null
            }
          }
        })()

        requestLocks.fetchPaymentsPromise = fetchPaymentsPromise
      }

      await requestLocks.fetchPaymentsPromise
    } catch (e) {
      console.error(e)
    }
  },

  async ADD_TO_CART(
    { commit, state, rootState, dispatch },
    { id, type, quantity, parentId, newLine, forCredits, custom },
  ) {
    const startedAt = Date.now()
    const requestConfig = getAddToCartRequestConfig(this.app)

    debugCart(this.app, 'ADD_TO_CART start', {
      id,
      quantity: Number(quantity),
      parentId,
      newLine: newLine || false,
      hasCustomData: !!custom,
      hasGuestCartToken: !!state.guestCartToken,
      hasAuthToken: !!rootState.auth.token,
      responseMode: requestConfig?.headers?.['X-Inspishop-Cart-Response-Mode'] || 'default',
      profiling: !!requestConfig?.params?.[cartFetchProfilingQueryKey],
    })

    try {
      const response = await this.$axios.$post(
        this.$env.STORE_URL + '/api/v1/cart/items',
        {
          id: id,
          type: type || 'Inspishop\\Shop\\Model\\Entities\\Product',
          amount: Number(quantity),
          parent_id: parentId ? Number(parentId) : null,
          newLine: newLine || false,
          credits: forCredits || false,
          custom: custom || [],
        },
        requestConfig,
      )

      debugCart(this.app, 'ADD_TO_CART response', {
        durationMs: Date.now() - startedAt,
        cart: getCartDataSummary(response.data?.cart),
        addedItemId: response.data?.addedItem?.id,
        addedItemProductId: response.data?.addedItem?.productId,
        addedItemVariantId: response.data?.addedItem?.variantId,
        changedAmount: response.data?.changedAmount,
      })

      let addedItem = null
      let usedFallbackFetch = false
      const addedItemLookupSource = createAddedItemLookupSource(response.data.addedItem, custom)

      if (isSummaryCartResponse(response.data.cart)) {
        addedItem = await dispatch('SET_CART_SUMMARY_DATA', {
          cartData: response.data.cart,
          addedItemData: response.data.addedItem,
        })
      } else {
        await dispatch('SET_CART_DATA', response.data.cart)
        addedItem = findCartItemByIdOrConfiguration(state.cartData.items, addedItemLookupSource)
      }

      if (!addedItem || !hasPositiveCartItemPrice(addedItem)) {
        usedFallbackFetch = true
        debugCart(this.app, 'ADD_TO_CART fallback fetch start', {
          durationMs: Date.now() - startedAt,
          hasAddedItem: !!addedItem,
          hasPositivePrice: hasPositiveCartItemPrice(addedItem),
        })

        await dispatch('FETCH_CART', { forceReload: true })
        const refreshedAddedItem = findCartItemByIdOrConfiguration(state.cartData.items, addedItemLookupSource)

        if (refreshedAddedItem) {
          addedItem = refreshedAddedItem
        }

        debugCart(this.app, 'ADD_TO_CART fallback fetch done', {
          durationMs: Date.now() - startedAt,
          hasRefreshedAddedItem: !!refreshedAddedItem,
          hasPositivePrice: hasPositiveCartItemPrice(addedItem),
          cart: getCartDataSummary(state.cartData),
        })
      }

      if (addedItem) {
        response.data.addedItem = addedItem
        eventBus.$emit(eventDefinitions.CART.ITEM.ADD, {
          addedAmount: response.data.changedAmount,
          item: addedItem,
        })
      }

      if (response.meta.custom && response.meta.custom['X-Inspishop-Guest-Cart']) {
        commit('SET_GUEST_CART_TOKEN', response.meta.custom['X-Inspishop-Guest-Cart'])
        dispatch('SET_AXIOS_GUEST_CART_TOKEN')
      }

      debugCart(this.app, 'ADD_TO_CART done', {
        durationMs: Date.now() - startedAt,
        usedFallbackFetch,
        hasAddedItem: !!addedItem,
        hasPositivePrice: hasPositiveCartItemPrice(addedItem),
        cart: getCartDataSummary(state.cartData),
      })

      return response.data
    } catch (e) {
      debugCart(this.app, 'ADD_TO_CART failed', {
        durationMs: Date.now() - startedAt,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      })
      errorFromResponse(e.response)
    }
  },

  async REMOVE_FROM_CART({ state, dispatch, rootGetters }, item) {
    const startedAt = Date.now()

    debugCart(this.app, 'REMOVE_FROM_CART start', {
      itemId: item?.id,
      amount: item?.amount,
    })

    try {
      const response = await this.$axios.$delete(
        this.$env.STORE_URL + '/api/v1/cart/items/' + item.id,
        getCartMutationRequestConfig(this.app),
      )
      await dispatch('SET_CART_DATA', response.data.cart)
      clearCartItemQuantityAnalytics(this.app, item?.id)
      eventBus.$emit(eventDefinitions.CART.ITEM.REMOVE, {
        item,
        removedAmount: Math.abs(response.data.changedAmount || item.amount || 1),
      })

      if (rootGetters['user/userIsLoggedIn'] && state.cartData.items && state.cartData.items.length === 0) {
        await dispatch('SET_CREDITS', 0)
      }

      debugCart(this.app, 'REMOVE_FROM_CART done', {
        durationMs: Date.now() - startedAt,
        itemId: item?.id,
        cart: getCartDataSummary(state.cartData),
      })

      return response.data
    } catch (e) {
      debugCart(this.app, 'REMOVE_FROM_CART failed', {
        durationMs: Date.now() - startedAt,
        itemId: item?.id,
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      })
      errorFromResponse(e.response)
    }
  },

  async UPDATE_ITEM({ state, dispatch }, { id, quantity }) {
    const startedAt = Date.now()

    debugCart(this.app, 'UPDATE_ITEM start', {
      itemId: id,
      quantity: Number(quantity),
    })

    try {
      const response = await this.$axios.$patch(
        this.$env.STORE_URL + '/api/v1/cart/items/' + id,
        {
          amount: Number(quantity),
        },
        getCartMutationRequestConfig(this.app),
      )
      await dispatch('SET_CART_DATA', response.data.cart)

      const updatedItem = findCartItemById(state.cartData.items, response.data.updatedItem?.id || id)

      if (updatedItem) {
        response.data.updatedItem = updatedItem
        const changedAmount = response.data.changedAmount

        queueCartItemQuantityAnalytics(this.app, updatedItem, changedAmount)
      }

      notifications.info(this.app.i18n.t('globals.cart.updateItem.success'))
      debugCart(this.app, 'UPDATE_ITEM done', {
        durationMs: Date.now() - startedAt,
        itemId: id,
        quantity: Number(quantity),
        changedAmount: response.data.changedAmount,
        cart: getCartDataSummary(state.cartData),
      })

      return response.data
    } catch (e) {
      debugCart(this.app, 'UPDATE_ITEM failed', {
        durationMs: Date.now() - startedAt,
        itemId: id,
        quantity: Number(quantity),
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      })
      errorFromResponse(e.response)
    }
  },

  async SET_CART_SUMMARY_DATA({ commit, dispatch, state }, { cartData, addedItemData }) {
    try {
      const enrichedData = await dispatch('UPDATE_DATA_WITH_VARIANTS', {
        data: { items: [addedItemData] },
        context: 'cart',
      })
      const addedItem = enrichedData?.items?.[0] || null

      if (!addedItem?.product || !addedItem?.shoppable || !addedItem?.variant) {
        await dispatch('FETCH_CART', { forceReload: true })

        return findCartItemByIdOrConfiguration(state.cartData.items, addedItemData)
      }

      const mergedCartData = hasCartPrices(cartData)
        ? mergeSummaryCartData(state.cartData, cartData, addedItem)
        : mergeSummaryLiteCartData(state.cartData, cartData, addedItem)

      commit('SET_CART_DATA', mergedCartData)

      if (hasCartPrices(cartData)) {
        dispatch('CHECK_SELECTED_GIFTS')
        dispatch('SET_GIFT_PRICES')
      }

      return findCartItemByIdOrConfiguration(mergedCartData.items, addedItem)
    } catch (e) {
      console.error(e)
    }
  },

  async SET_CART_DATA({ commit, dispatch, state }, cartData) {
    const startedAt = Date.now()

    debugCart(this.app, 'SET_CART_DATA start', {
      incomingCart: getCartDataSummary(cartData),
      currentCart: getCartDataSummary(state.cartData),
      isLoaded: state.isLoaded,
    })

    try {
      if (cartData) {
        cartData = normalizeCartDataItems(cartData)
        debugCart(this.app, 'SET_CART_DATA normalized', {
          cart: getCartDataSummary(cartData),
        })
        cartData = (await dispatch('UPDATE_DATA_WITH_VARIANTS', { data: cartData, context: 'cart' })) || cartData
        cartData = {
          ...cartData,
          deferredTotals: false,
        }
        debugCart(this.app, 'SET_CART_DATA enriched', {
          durationMs: Date.now() - startedAt,
          cart: getCartDataSummary(cartData),
        })
        const invalidItems = cartData.items.filter(item => !item.product || !item.shoppable || !item.variant)
        if (invalidItems && invalidItems.length > 0) {
          console.warn(
            '[cart] Product data enrichment failed for cart items; keeping backend cart intact.',
            invalidItems.map(item => ({
              id: item.id,
              productId: item.productId,
              variantId: item.variantId,
            })),
          )
        }
        commit('SET_CART_DATA', cartData)
        debugCart(this.app, 'SET_CART_DATA committed', {
          durationMs: Date.now() - startedAt,
          isLoaded: state.isLoaded,
          cart: getCartDataSummary(state.cartData),
        })
        const refreshCheckoutState = async () => {
          debugCart(this.app, 'SET_CART_DATA refresh checkout state start')
          await Promise.all([
            dispatch('FETCH_SHIPPERS', { force: true }),
            dispatch('FETCH_PAYMENTS', { force: true }),
            dispatch('FETCH_AVAILABLE_GIFTS'),
          ])
          debugCart(this.app, 'SET_CART_DATA refresh checkout state done', {
            shippersCount: state.shippers.length,
            paymentsCount: state.payments.length,
            giftsCount: state.availableGifts?.length || 0,
          })

          if (state.input.shipper) {
            commit('SET_CART_INPUT_FIELD', {
              field: 'shipper',
              value: state.shippers.find(shipper => shipper.id === state.input.shipper.id),
            })
          }

          if (state.input.payment) {
            commit('SET_CART_INPUT_FIELD', {
              field: 'payment',
              value: state.payments.find(payment => payment.id === state.input.payment.id),
            })
          }
        }

        const currentRoute = getCurrentRoute(this.app)

        if (isCheckoutRoute(currentRoute)) {
          await refreshCheckoutState()
          dispatch('CHECK_SELECTED_PAYMENT')
          dispatch('CHECK_SELECTED_SHIPPER')
        }

        dispatch('CHECK_SELECTED_GIFTS')
        dispatch('SET_GIFT_PRICES')
      } else {
        debugCart(this.app, 'SET_CART_DATA skipped empty payload')
      }
    } catch (e) {
      console.error(e)
      debugCart(this.app, 'SET_CART_DATA failed', {
        durationMs: Date.now() - startedAt,
        message: e?.message,
        incomingCart: getCartDataSummary(cartData),
        currentCart: getCartDataSummary(state.cartData),
        isLoaded: state.isLoaded,
      })
      if (!state.isLoaded) {
        commit('SET_CART_DATA', ensureCartDataProductData(normalizeCartDataItems(cartData || state.cartData || {})))
        debugCart(this.app, 'SET_CART_DATA fallback committed', {
          isLoaded: state.isLoaded,
          cart: getCartDataSummary(state.cartData),
        })
      }
    }
  },

  async UPDATE_DATA_WITH_VARIANTS({ dispatch }, payload) {
    const context = payload && payload.context ? payload.context : null
    const data = normalizeCartDataItems(context ? payload.data : payload)
    const startedAt = Date.now()

    try {
      if (data && data.items) {
        const productIds = new Set()
        const variantIds = new Set()

        function collectIds(item) {
          if (item.productId != null) {
            productIds.add(item.productId)
          }

          if (item.variantId != null) {
            variantIds.add(item.variantId)
          }

          if (item.items) {
            for (const childItem of item.items) {
              collectIds(childItem)
            }
          }
        }

        for (const item of data.items) {
          collectIds(item)
        }

        debugCart(this.app, 'UPDATE_DATA_WITH_VARIANTS load products start', {
          context,
          itemsCount: data.items.length,
          productIdsCount: productIds.size,
          variantIdsCount: variantIds.size,
        })

        const products =
          (await resolveWithTimeout(
            dispatch(
              'product/LOAD_PRODUCTS',
              context
                ? {
                    ids: [...productIds],
                    variantIds: [...variantIds],
                    context,
                  }
                : {
                    ids: [...productIds],
                  },
              { root: true },
            ),
            [],
            cartProductEnrichmentTimeoutMs,
          )) || []

        debugCart(this.app, 'UPDATE_DATA_WITH_VARIANTS load products done', {
          durationMs: Date.now() - startedAt,
          context,
          productsCount: products.length,
          productIdsCount: productIds.size,
          variantIdsCount: variantIds.size,
        })

        const productMap = new Map()
        const variantMap = new Map()

        for (const product of products) {
          productMap.set(String(product.id), product)
          variantMap.set(
            String(product.id),
            new Map((product.variants || []).map(variant => [String(variant.id), variant])),
          )
        }

        function updateItem(item) {
          const productKey = item.productId != null ? String(item.productId) : null
          const variantKey = item.variantId != null ? String(item.variantId) : null
          const existingProduct = item.product || null
          const existingVariant = item.variant || item.shoppable || null
          const product = productKey ? productMap.get(productKey) || existingProduct : existingProduct
          const variant =
            productKey && variantKey ? variantMap.get(productKey)?.get(variantKey) || existingVariant : existingVariant

          if (variant) {
            item.shoppable = variant
            item.variant = variant
          }
          if (product) {
            item.product = product
          }

          if (item.items) {
            for (const childItem of item.items) {
              updateItem(childItem)
            }
          }
        }

        for (const item of data.items) {
          updateItem(item)
        }
      }

      const ensuredData = ensureCartDataProductData(data)
      debugCart(this.app, 'UPDATE_DATA_WITH_VARIANTS done', {
        durationMs: Date.now() - startedAt,
        cart: getCartDataSummary(ensuredData),
      })

      return ensuredData
    } catch (e) {
      console.error(e)
      const ensuredData = ensureCartDataProductData(data)
      debugCart(this.app, 'UPDATE_DATA_WITH_VARIANTS failed', {
        durationMs: Date.now() - startedAt,
        message: e?.message,
        cart: getCartDataSummary(ensuredData),
      })

      return ensuredData
    }
  },

  async PLACE_ORDER(actionContext) {
    const { commit, state, dispatch, rootState, rootGetters, getters } = actionContext
    try {
      const name = this.$themeSettings.global.userFullName
        ? state.input.fullName
        : state.input.firstName + ' ' + state.input.lastName
      const shippingName = this.$themeSettings.global.userFullName
        ? state.input.shippingFullName
        : state.input.shippingFirstName + ' ' + state.input.shippingLastName

      const getDefaultAddress = key => {
        const searchKey = key !== 'country_code' ? key : 'countryCode'
        return state.input.shipperBranch
          ? state.input.shipperBranch.address[searchKey]
          : rootState.globals.contact.address[searchKey]
      }
      const setShipperAddress = addressData => {
        for (const val of ['street', 'city', 'postcode', 'country_code']) {
          addressData[val] = getDefaultAddress(val)
        }
      }
      const isRegistration = getters.isRegistration
      const billingCountry = state.input.billingCountry || state.input.country
      const data = {
        name,
        firstName: state.input.firstName,
        lastName: state.input.lastName,
        email: state.input.email,
        phone: state.input.phone,
        billing_address: {
          street: state.input.billingHouseNumber
            ? `${state.input.billingStreet} ${state.input.billingHouseNumber}`
            : state.input.billingStreet,
          city: state.input.billingCity,
          postcode: state.input.billingPostcode,
          country_code: billingCountry,
        },
        business: state.input.isBusiness
          ? {
              business_name: state.input.businessName,
              TIN: state.input.tin,
              VATIN: state.input.vatin,
            }
          : null,
        shipping: state.input.shipToShippingAddress
          ? {
              name: shippingName.replace(' ', '') !== '' ? shippingName : name,
              address: {
                street: state.input.houseNumber
                  ? `${state.input.street} ${state.input.houseNumber}`
                  : state.input.street,
                city: state.input.city,
                postcode: state.input.postcode,
                country_code: state.input.country,
              },
            }
          : {
              name: shippingName.replace(' ', '') !== '' ? shippingName : name,
              address: {
                street: state.input.billingHouseNumber
                  ? `${state.input.billingStreet} ${state.input.billingHouseNumber}`
                  : state.input.billingStreet,
                city: state.input.billingCity,
                postcode: state.input.billingPostcode,
                country_code: billingCountry,
              },
            },
        custom: state.input.custom,
        comment: state.input.note,
        shipper_comment: state.input.note_for_shipper,
        payment_id: state.input.payment ? state.input.payment.id : null,
        shipper_id: state.input.shipper ? state.input.shipper.id : null,
        branch_id: state.input.shipperBranch ? state.input.shipperBranch.id : null,
        terms: true,
        cardRequest: state.input.cardRequest,
        order_gifts: state.selectedGifts.map(g => g.id),
        newsletter: state.input.newsletter,
        heureka_allow: state.input.complianceHeureka,
        zbozicz_allow: state.input.complianceZbozi,
        magazine: state.input.complianceMagazine,
        phone_notification: state.input.complianceSmsMarketing,
        delivery_time_id: state.input.deliveryTimeSlot ? state.input.deliveryTimeSlot.id : null,
        ...(isRegistration
          ? {
              password: state.input.password,
              password_confirmation: state.input.passwordConfirmation,
              registration: true,
            }
          : {}),
      }
      if (state.input.shippingBusinessName && state.input.shippingBusinessName.replace(/\s/g, '').length > 0) {
        if (
          state.input.isBusiness
            ? !!this.$themeSettings.components.PageCheckoutInfo.input.shippingBusinessName.enableWithBusiness
            : true
        ) {
          data.shipping.business_name = state.input.shippingBusinessName
        }
      }
      if (
        state.input.shipToShippingAddress &&
        state.input.shippingPhone &&
        state.input.shippingPhone.replace(/\s/g, '').length > 0
      ) {
        data.shipping.phone = state.input.shippingPhone
      }
      if (state.input.formalTitle) {
        data.custom.formalTitle = state.input.formalTitle
      }
      if (state.input.vatinsvk) {
        data.custom.ICDPH = state.input.vatinsvk
      }
      if (state.input.dateOfBirth) {
        data.custom.dateOfBirth = state.input.dateOfBirth
      }
      if (state.input.companyName) {
        data.custom.companyName = state.input.companyName
      }

      if (rootGetters['utm/getUtmParams']) {
        data.utm = rootGetters['utm/getUtmParams']
      }
      await hookDispatch(hookDefinitions.CART.PLACE_ORDER.DATA_PROCESS, { actionContext, data })
      const response = await this.$axios.$post(
        this.$env.STORE_URL + '/api/v1/cart/place',
        data,
        getPlaceOrderRequestConfig(this.app),
      )
      const orderData = reduceOrderProductStorage(
        (await dispatch('UPDATE_DATA_WITH_VARIANTS', response.data)) || response.data,
      )

      commit('SET_PLACED_ORDER', orderData)
      commit('SET_EMPTY_CART')
      commit('SET_EMPTY_GIFTS')
      return response
    } catch (e) {
      console.log('PLACE_ORDER error:', e)
      return e.response
    }
  },
}

const mutations = {
  ...parentMutations,
  SET_GUEST_CART_TOKEN(state, token) {
    state.guestCartToken = token

    if (!process.client) {
      return
    }

    if (token) {
      jsCookie.set(guestCartStorageCookie, token, { ...guestCartCookieOptions, expires: 1 })
      setClientStorage(guestCartStorageKey, token)
    } else {
      jsCookie.remove(guestCartStorageCookie, guestCartCookieOptions)
      jsCookie.remove(guestCartBackendCookie, guestCartCookieOptions)
      setClientStorage(guestCartStorageKey, null)
    }
  },
}

export { actions, getters, mutations, state }
